import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import TempoLogo from '@/assets/tempo-logo.svg';
import { ExportModeProvider } from '@/components/ui/swimlane/context';
import { Colors } from '@/lib/utils/common/colors';
import { LegendFooter } from '../published/LegendFooter';
import { PublishedSwimlane } from '../published/PublishedSwimlane';
import { getEffectivePreferences } from './export-utils';
import { useExportSwimlane } from './useExportSwimlane';

/**
 * Search parameter schema for PDF export route.
 * Note: transparent parameter is NOT supported for PDF exports.
 */
export const internalSwimlaneExportSearchSchema = z.object({
  token: z.string().min(1, 'Token cannot be empty'),
  title: z.coerce.string().optional(),
  branding: z.coerce.string().optional(),
  date: z.coerce.string().optional(),
  legend: z.coerce.string().optional(),
  vision: z.string().optional(),
});

export type InternalSwimlaneExportSearchParams = z.infer<typeof internalSwimlaneExportSearchSchema>;

export interface ExportPdfPageProps {
  /**
   * The viewId from the route parameter
   */
  viewId: number;
  /**
   * The visualizationId from the route parameter
   */
  visualizationId: number;
  /**
   * Search parameters from the URL query string
   */
  searchParams: InternalSwimlaneExportSearchParams;
}

/**
 * PDF export page component for token-authenticated visualizations.
 * This component is optimized for PDF single-page capture:
 * - No sticky headers (clean print layout)
 * - No transparent background support
 * - Uses data-export-target="header" on the header div so the backend can detect header presence
 *
 * Unlike ExportSwimlanePage which is optimized for PNG tiled screenshot capture,
 * this component renders a clean, print-ready view suitable for Playwright's page.pdf().
 */
export function ExportPdfPage({ viewId, visualizationId, searchParams }: ExportPdfPageProps) {
  const { t } = useTranslation();
  const result = useExportSwimlane({
    viewId,
    visualizationId,
    token: searchParams.token,
  });

  // Set page title for better PDF metadata
  useEffect(() => {
    if (result.status === 'success') {
      document.title = result.data.metadata.title;
    }
  }, [result]);

  // Handle loading and error states
  switch (result.status) {
    case 'loading':
      return (
        <output className="flex items-center justify-center h-screen text-xl">
          {t(($) => $.views.published.loading)}
        </output>
      );

    case 'error':
      return (
        <div
          className="flex items-center justify-center h-screen text-xl text-text-error"
          role="alert"
        >
          {t(($) => $.views.published.errorLoading, { message: result.message })}
        </div>
      );

    case 'success':
      break; // Fall through to render content
  }

  const { metadata, legendItems } = result.data;

  const preferences = getEffectivePreferences(metadata, searchParams);

  const visionStatement =
    searchParams.vision !== undefined ? searchParams.vision : metadata.visionStatement;

  // Determine if header section should be shown
  const showHeader = preferences.showTitle || preferences.showVisionStatement;

  // Format publish date with fallback to createdAt
  // Note: Using 'en-US' locale consistently for all exports to ensure uniform formatting
  // across all exported/published content, regardless of user's browser locale.
  // This matches the behavior in ExportSwimlanePage.tsx and PublishedSwimlanePage.tsx.
  const publishDateValue = metadata.publishedAt || metadata.createdAt;
  const publishDate = publishDateValue
    ? new Date(publishDateValue).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  /**
   * Container height for AutoSizer layout.
   * This is a LAYOUT PLACEHOLDER only - NOT the actual PDF page size.
   *
   * The backend measures actual rendered content dimensions and uses those for PDF generation.
   * Maximum safe PDF size: ~19,200 pixels per dimension (200 inches = 14,400 PDF points).
   *
   * This 20000px container (208 inches, 15,000 points) exceeds PDF limits, but that's safe because:
   * - It's only used for React AutoSizer to lay out content
   * - Backend measures the actual content (typically 3000-5000px)
   * - Backend validates measured dimensions don't exceed PDF format limits
   */
  const CONTAINER_HEIGHT_PX = 20000;

  return (
    <ExportModeProvider mode="pdf">
      {/* Outer container with explicit height for AutoSizer to measure against */}
      <div style={{ height: `${CONTAINER_HEIGHT_PX}px`, width: '100%' }}>
        <div
          data-export-target="swimlane-export-root"
          style={{
            backgroundColor: Colors.white.bg,
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header Section - Title and Vision Statement */}
          {showHeader && (
            <div className="px-8 pt-6 pb-4" data-export-target="header" style={{ flexShrink: 0 }}>
              {preferences.showTitle && (
                <h1 className="text-2xl font-bold text-text-neutral-primary mb-2">
                  {metadata.title}
                </h1>
              )}
              {preferences.showVisionStatement && visionStatement && (
                <div className="max-h-32 overflow-y-auto">
                  <p className="text-sm text-text-neutral-secondary whitespace-pre-wrap">
                    {visionStatement}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Swimlane Content - flex-1 gives remaining space for AutoSizer to measure */}
          <div style={{ flex: '1 1 auto', minHeight: 0, width: '100%' }}>
            <PublishedSwimlane data={result.data} transparentBackground={false} />
          </div>

          {/* Footer Section - Legend, Publish Date, and Branding */}
          {(preferences.showPublishDate ||
            preferences.showTempoBranding ||
            preferences.showLegend) && (
            <div className="py-4 w-full" style={{ flexShrink: 0 }}>
              {/* Legend at top of footer (centered) */}
              {preferences.showLegend && legendItems.length > 0 && (
                <nav
                  aria-label="Roadmap legend"
                  className="w-full mb-3"
                  data-component="legend-wrapper"
                >
                  <LegendFooter items={legendItems} />
                </nav>
              )}

              {/* Bottom row: Publish date (left) and Branding (right) */}
              <div className="flex items-center justify-between px-8">
                <div className="text-sm text-text-neutral-secondary">
                  {preferences.showPublishDate && publishDate && (
                    <time dateTime={publishDateValue}>
                      {t(($) => $.views.published.publishedOn, { date: publishDate })}
                    </time>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {preferences.showTempoBranding && (
                    <div className="flex items-center gap-2">
                      <img src={TempoLogo} alt="Tempo" className="h-6 w-6" />
                      <span className="text-sm font-semibold text-text-neutral-primary">
                        {t(($) => $.views.export.tempoBrandingName)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ExportModeProvider>
  );
}
